import { logger } from '@magicschool/logger';
import { isSupported, Upload } from 'tus-js-client';
import { v4 as uuid } from 'uuid';
import { getAssistantUploadPaths } from '@/features/rag/actions/getAssistantUploadPaths';
import { clientSupabase } from '@/util/supabase';

export const uploadFiles = async ({
  files,
  ragAssistantId,
  isPolling,
  onError,
  onProgress,
  onSuccess,
  onBrowserNotSupported,
}: {
  files: File[];
  ragAssistantId?: string | null;
  isPolling: boolean;
  onError?: (file: File, error: string) => void;
  onProgress?: (file: File, progress: number) => void;
  onSuccess?: (file: File, fileId: string) => void;
  onBrowserNotSupported?: () => void;
}) => {
  if (!isSupported) {
    // Not exiting early as we do not have a fallback for now
    onBrowserNotSupported?.();
  }

  if (!ragAssistantId) {
    logger.info('Missing rag_assistant_id. Will need to queue.');
    return { queued: true, results: [] };
  }

  // Grab the user session for the upload
  const {
    data: { session },
  } = await clientSupabase.auth.getSession();
  if (!session?.access_token) throw new Error('Failed to get Supabase session');

  // Resolve storage paths on the server. getRAGFilePath uses Node's `crypto` (via generateHashedName),
  // which is unavailable in the browser — so path derivation must happen server-side, not here.
  const { data: filePaths, error: pathsError } = await getAssistantUploadPaths(
    ragAssistantId,
    files.map((f) => f.name),
  );
  if (pathsError || !filePaths) {
    throw new Error(`Error resolving upload paths for rag assistant ${ragAssistantId}.`);
  }

  const missingPaths = files.filter((f) => !filePaths[f.name]).map((f) => f.name);
  if (missingPaths.length > 0) {
    throw new Error(`No storage path resolved for ${missingPaths.join(', ')}`);
  }

  // Kick off parallel uploads
  const uploadResults = await Promise.allSettled<{ fileName: string; fileId?: string; error?: string }>(
    files.map((file) => {
      if (isPolling) {
        logger.debug(`Upload queued due to missing rag_assistant_id`);
        return Promise.resolve({ fileName: file.name });
      }

      const filePath = filePaths[file.name];
      // Guaranteed by the missingPaths check above; narrow for TypeScript/Biome.
      if (!filePath) {
        return Promise.reject({ fileName: file.name, error: `No storage path resolved for ${file.name}` });
      }
      logger.debug(`Uploading file ${file.name} to path ${filePath}`);

      return new Promise((resolve, reject) => {
        const upload = new Upload(file, {
          endpoint: `${process.env.NEXT_PUBLIC_SUPABASE_URL}/storage/v1/upload/resumable`,
          retryDelays: [0, 3000, 5000, 10000, 20000],
          headers: {
            // The Supabase API gateway requires the `apikey` header. The Supabase JS client adds it to
            // every storage request automatically, but this raw tus-js-client upload bypasses the client,
            // so we must set it explicitly — without it the gateway can reject the upload before it
            // reaches storage (observed as a silent upload failure with nothing landing in the bucket).
            apikey: process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY ?? '',
            authorization: `Bearer ${session.access_token}`,
            'x-upsert': 'true',
          },

          uploadDataDuringCreation: true,
          removeFingerprintOnSuccess: true,
          metadata: {
            bucketName: 'assistant-docs',
            objectName: filePath,
            contentType: file.type,
          },
          chunkSize: 6 * 1024 * 1024,
          onError: (error) => {
            logger.error(`Upload failed for ${file.name}`, error);
            onError?.(file, `Upload failed for ${file.name}`);
            reject({ fileName: file.name, error: error.message });
          },
          onProgress: (bytesUploaded, bytesTotal) => {
            const pct = ((bytesUploaded / bytesTotal) * 100).toFixed(2);
            logger.debug(`Uploading ${file.name}: ${pct}%`);
            onProgress?.(file, Number.parseFloat(pct));
          },
          onSuccess: () => {
            logger.debug(`Upload complete: ${file.name}`);
            const fileId = uuid();
            onSuccess?.(file, fileId);
            resolve({ fileName: file.name, fileId });
          },
          onShouldRetry: (err, retryAttempt) => {
            const status = err.originalResponse ? err.originalResponse.getStatus() : 0;
            logger.error(`Upload failed for ${file.name}. Status: ${status}. Retry attempt: ${retryAttempt}. Error:`, err.message);
            logger.debug('Request', err.originalRequest);
            logger.debug('Response', err.originalResponse);

            if (retryAttempt > 5) {
              return false;
            }

            // Do not retry if the status is a 403.
            if (status === 403) {
              return false;
            }

            // For any other status code, we retry.
            return true;
          },
        });

        // Attempt to resume if there's an old partial
        upload.findPreviousUploads().then((previousUploads) => {
          if (previousUploads.length) {
            upload.resumeFromPreviousUpload(previousUploads[0]);
          }
          upload.start();
        });
      });
    }),
  );

  return { queued: false, results: uploadResults };
};
